import { ActivityDTO, ActivityType } from 'src/models/dto/ActivityDTO'
import { ItemDTO } from 'src/models/dto/items/ItemDTO'
import { PageItemSummary } from 'src/pages/module-builder/buckets-and-cups-editor/BucketsAndCupsVisualizer'
import { ModuleService } from 'src/services/backend/ModuleService'
import { ItemPoolService } from 'src/services/item-pools/ItemPoolService'

/**
 * A page in the ItemPool lives inside an item container module (a module of type
 * ItemsContainer). Its item-group activity carries page labels plus the items on
 * the page. A cup's `itemIds` are those page labels. These helpers resolve a page
 * label to a summary of the items on that page, given the item container modules.
 *
 * Item container modules are fetched the same way as any module: via the
 * GetModuleVersion API (`ModuleService.loadModuleVersionDTO`).
 */

/** Minimal structural shape needed to resolve pages (a fetched module's content). */
export interface ItemContainerLike {
    workflow: ActivityDTO[]
}

function summarizeContextBox(): PageItemSummary {
    return { type: 'ContextBox' }
}

function summarizeItem(item: ItemDTO): PageItemSummary {
    return { type: item.itemType }
}

/** Build a label -> page-items map from a single item container module's content. */
export function extractContainerPages(
    container: ItemContainerLike
): Record<string, PageItemSummary[]> {
    const pages: Record<string, PageItemSummary[]> = {}
    for (const activity of container.workflow) {
        const itemGroup = activity.moduleChild
        if (itemGroup.type !== ActivityType.LaunchItemGroup) {
            continue
        }
        const summaries: PageItemSummary[] = []
        if (itemGroup.contextBox) {
            summaries.push(summarizeContextBox())
        }
        ;(itemGroup.items ?? []).forEach((item) => summaries.push(summarizeItem(item)))

        const keys = new Set<string>(itemGroup.labels ?? [])
        if (itemGroup.name) {
            keys.add(itemGroup.name)
        }
        keys.forEach((key) => {
            pages[key] = summaries
        })
    }
    return pages
}

/** Build a page resolver across several item container modules. */
export function buildPageResolver(
    containers: ItemContainerLike[]
): (pageLabel: string) => PageItemSummary[] {
    const merged: Record<string, PageItemSummary[]> = {}
    containers.forEach((container) => Object.assign(merged, extractContainerPages(container)))
    return (pageLabel: string) => merged[pageLabel] ?? []
}

/**
 * Fetch the given item container modules (via the GetModuleVersion API) and
 * return a resolver from a page label to the items on that page.
 */
export async function fetchItemContainerResolver(
    itemContainerVersionIds: string[]
): Promise<(pageLabel: string) => PageItemSummary[]> {
    const modules = await Promise.all(
        itemContainerVersionIds.map((versionId) => ModuleService.loadModuleVersionDTO(versionId))
    )
    return buildPageResolver(modules.map((moduleVersion) => moduleVersion.content))
}

/**
 * Resolve an ItemPool version id to a page resolver: reads the pool's item
 * container version ids and builds a label -> page-items resolver from them.
 * A falsy or container-less pool yields a resolver that returns no items.
 */
export async function loadPoolPageResolver(
    poolVersionId: string
): Promise<(pageLabel: string) => PageItemSummary[]> {
    const metadata = poolVersionId
        ? await ItemPoolService.getItemPoolMetadata(poolVersionId)
        : undefined
    const containerVersionIds = (metadata?.itemContainers ?? []).map(
        (container) => container.versionId
    )
    return fetchItemContainerResolver(containerVersionIds)
}
